import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  Database, RefreshCw, CheckCircle2, XCircle, TrendingUp, 
  BarChart3, Globe, Zap, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SOURCE_INFO = {
  yahoo: { name: 'Yahoo Finance', color: 'purple', description: 'Real-time quotes via yfinance' },
  nasdaq: { name: 'NASDAQ', color: 'blue', description: 'Official NASDAQ exchange data' },
  cboe: { name: 'CBOE', color: 'green', description: 'Chicago Board Options Exchange' },
  barchart: { name: 'Barchart', color: 'orange', description: 'Market data and analytics' },
};

const DataSourcesPanel = ({ onPriceUpdate }) => {
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchMultiSourceData = async () => {
    if (!symbol.trim()) {
      toast.error('Please enter a stock symbol');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/data/multi-source/${symbol.toUpperCase()}`);
      setData(response.data);
      
      // Count available sources
      const availableCount = Object.values(response.data.sources || {}).filter(s => s.available).length;
      toast.success(`Fetched data from ${availableCount} sources for ${symbol.toUpperCase()}`);
      
      // Callback with best price if provided
      if (onPriceUpdate && response.data.price_comparison?.average) {
        onPriceUpdate(response.data.price_comparison.average);
      }
    } catch (err) {
      console.error('Error fetching multi-source data:', err);
      setError(err.response?.data?.detail || 'Failed to fetch data');
      toast.error('Failed to fetch data from sources');
    } finally {
      setLoading(false);
    }
  };

  const getSourceBadgeColor = (sourceId) => {
    const colors = {
      yahoo: 'bg-purple-100 text-purple-700 border-purple-300',
      nasdaq: 'bg-blue-100 text-blue-700 border-blue-300',
      cboe: 'bg-green-100 text-green-700 border-green-300',
      barchart: 'bg-orange-100 text-orange-700 border-orange-300',
    };
    return colors[sourceId] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Card className="glass-effect border-0 shadow-lg">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-lg">
          <Database className="h-5 w-5 mr-2 text-indigo-600" />
          Multi-Source Data
        </CardTitle>
        <CardDescription>
          Compare option chain data from multiple providers
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search Input */}
        <div className="flex space-x-2">
          <Input
            placeholder="Enter symbol (e.g., AAPL)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            onKeyPress={(e) => e.key === 'Enter' && fetchMultiSourceData()}
            className="flex-1"
          />
          <Button 
            onClick={fetchMultiSourceData} 
            disabled={loading}
            className="bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Globe className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
            <AlertTriangle className="h-4 w-4 mr-2" />
            {error}
          </div>
        )}

        {/* Results */}
        {data && (
          <div className="space-y-4">
            {/* Price Comparison */}
            {data.price_comparison?.available && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center">
                  <TrendingUp className="h-4 w-4 mr-1" />
                  Price Comparison - {data.symbol}
                </h4>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-xs text-gray-500">Average</p>
                    <p className="text-lg font-bold text-green-600">
                      ${data.price_comparison.average?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Min</p>
                    <p className="text-lg font-bold text-blue-600">
                      ${data.price_comparison.min?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Max</p>
                    <p className="text-lg font-bold text-purple-600">
                      ${data.price_comparison.max?.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Spread</p>
                    <p className="text-lg font-bold text-orange-600">
                      ${data.price_comparison.spread?.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Source Status */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-gray-700">Data Sources</h4>
              {Object.entries(data.sources || {}).map(([sourceId, sourceData]) => (
                <div 
                  key={sourceId}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    sourceData.available 
                      ? 'bg-white border-gray-200' 
                      : 'bg-gray-50 border-gray-200 opacity-60'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    {sourceData.available ? (
                      <CheckCircle2 className="h-5 w-5 text-green-500" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                    <div>
                      <Badge variant="outline" className={getSourceBadgeColor(sourceId)}>
                        {SOURCE_INFO[sourceId]?.name || sourceId}
                      </Badge>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {SOURCE_INFO[sourceId]?.description}
                      </p>
                    </div>
                  </div>
                  
                  {sourceData.available && sourceData.current_price && (
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        ${typeof sourceData.current_price === 'number' 
                          ? sourceData.current_price.toFixed(2) 
                          : sourceData.current_price}
                      </p>
                      {sourceId === data.primary_source && (
                        <Badge className="text-[10px] bg-indigo-100 text-indigo-700">
                          PRIMARY
                        </Badge>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Chain Data Stats */}
            {data.sources?.yahoo?.available && (
              <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                <div className="flex justify-between">
                  <span>Calls: {data.sources.yahoo.calls?.length || 0}</span>
                  <span>Puts: {data.sources.yahoo.puts?.length || 0}</span>
                  <span>Expirations: {data.sources.yahoo.expirations?.length || 0}</span>
                </div>
              </div>
            )}

            {/* Timestamp */}
            <p className="text-xs text-gray-400 text-center">
              Last updated: {new Date(data.timestamp).toLocaleString()}
            </p>
          </div>
        )}

        {/* Instructions */}
        {!data && !loading && (
          <div className="text-center py-4 text-gray-500">
            <BarChart3 className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">Enter a symbol to fetch data from multiple sources</p>
            <p className="text-xs mt-1">Sources: Yahoo Finance, NASDAQ, CBOE, Barchart</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DataSourcesPanel;
